/**
 * depthExtractor.js — ПОСТАВЩИК ГЛУБИНЫ для кинематографического слоя.
 * Mikhmel Cornerstone4D DICOM Viewer Pro — 10.19.18
 *
 * ИСТОРИЯ ЭТОГО ФАЙЛА, коротко и честно.
 *
 * Он назывался «извлекателем» и делал вид, что достаёт z-буфер из vtk.js. На
 * самом деле он читал ЦВЕТНОЙ буфер объёмного окна (`readPixels` с `gl.RGBA`),
 * сворачивал RGB в яркость и отдавал это наружу как `{ isFloat: true }` — то
 * есть как настоящую карту глубины, а в журнал писал «hardware depth active».
 * Из этой выдумки SSAO изобретал затенение: замер на живой сборке — включение
 * ОДНОГО SSAO роняло заполненность кадра 27,8% → 15,6% и яркость 15,7 → 4,4,
 * кадр темнел в 3,5 раза. В 10.19.17 подделку убрал, но глубины не стало вовсе,
 * и вместе с ней отключились затенение, отражения, туман и глубина резкости.
 *
 * ТЕПЕРЬ ГЛУБИНА НАСТОЯЩАЯ, и она СЧИТАЕТСЯ, а не «извлекается»:
 * src/rendering/depthFromVolume.js прогоняет луч на каждый пиксель по тому же
 * объёму и по той же камере, что рисуют картинку, и берёт расстояние до первой
 * значимой непрозрачности. Почему нельзя взять готовую у vtk.js — разобрано по
 * его исходникам в depthMath.js: объёмный шейдер не пишет gl_FragDepth вообще.
 *
 * ЧТО ЗДЕСЬ. Подписка на события окна, прореживание по времени (глубина нужна
 * при смене камеры, а не 60 раз в секунду), передача карты в рендерер и —
 * отдельно — передача МАТРИЦ КАМЕРЫ. Матрицы никто в программе не подавал
 * никогда: `setCameraMatrices` не вызывался ни из одного места, поэтому
 * объёмный свет, тени, векторы движения и вторичное освещение молча ничего не
 * делали, сколько бы их ни включали.
 */

import { log } from '../utils/logger.js';
import { emit, EVENTS } from '../core/eventBus.js';
import { ГлубинаИзОбъёма, непрозрачностьОкна, проверкаНормалейНаСфере } from './depthFromVolume.js';
import { разобратьКамеру, матрицыКамеры } from './depthMath.js';

let _active = false;
let _viewport = null;
let _cinematic = null;
let _расчёт = null;
let _onRenderHandler = null;
let _таймер = null;
let _последний = 0;
let _подписьКамеры = '';

/** Не чаще этого глубина не пересчитывается: между кадрами она не меняется. */
export const ПАУЗА_МС = 220;

export const depthDiagnostics = {
  /** Почему глубины нет. Пусто — значит глубина есть. */
  причина: '',
  /** Настоящая ли глубина. Отдельного значения «почти настоящая» не бывает. */
  isUsingRealDepth: false,
  /** Сколько миллисекунд заняла последняя карта. */
  extractionTimeMs: 0,
  /** Сколько карт посчитано за сеанс. */
  карт: 0,
  /** Доля пикселей, где луч во что-то попал. */
  доляПопаданий: 0,
  /** [10.19.21] Доля попаданий, у которых есть годная нормаль из объёма.
   *  Ноль при живой глубине означает, что нормали не читаются — и тогда
   *  полусферный SSAO и N·L честно не включаются, а не считают по мусору. */
  доляНормалей: 0,
  размер: '',
  /** Диапазон значений объёма и область определения функции непрозрачности.
   *  Если они в разных единицах (например, объём в «сырых» значениях, а таблица
   *  в единицах Хаунсфилда), воздух окажется непрозрачным, и глубина упрётся в
   *  ближнюю плоскость по всему кадру. Такое надо ВИДЕТЬ числами. */
  диапазонДанных: null,
  диапазонТаблицы: null,
  /** Откуда взяты значения объёма: должен быть тот же массив, что у маппера. */
  источникСкаляров: '',
};

/** Объёмное окно раскладки. */
function _объёмноеОкно(viewport) { return viewport || null; }

/** Данные объёма из кэша Cornerstone: скаляры, размеры, шаг, начало, направление. */
async function _объёмОкна(vp) {
  try {
    const uid = vp.getActorUIDs?.()?.[0] || null;
    const cs = await import('@cornerstonejs/core');
    let volume = null;
    // Идентификатор актора в объёмном окне — это идентификатор объёма.
    if (uid) volume = cs.cache.getVolume?.(uid) || null;
    if (!volume) {
      const ids = cs.cache.getVolumes?.()?.map?.((v) => v.volumeId) || [];
      for (const id of ids) { const v = cs.cache.getVolume(id); if (v) { volume = v; break; } }
    }
    if (!volume) return { ок: false, причина: 'объём не найден в кэше' };

    // ПОРЯДОК ВАЖЕН, и это выяснилось замером. Сначала берётся массив
    // imageData — ровно тот, который сэмплирует объёмный маппер vtk.js. Раньше
    // первым шёл voxelManager, и на синтетической серии он отдал значения
    // 20…627 (сырые, до пересчёта), тогда как функция непрозрачности задана в
    // единицах Хаунсфилда (−1000…3000). Воздух со значением 20 попадал в
    // область мягких тканей, 79% вокселей оказывались непрозрачными, и глубина
    // упиралась в ближнюю плоскость по всему кадру. Считать надо по тем же
    // числам, по которым рисует маппер, иначе глубина относится к другой
    // картинке.
    let скаляры = null;
    let источникСкаляров = '';
    try {
      скаляры = volume.imageData?.getPointData?.()?.getScalars?.()?.getData?.();
      if (скаляры) источникСкаляров = 'imageData';
    } catch (e) { /* следующий путь */ }
    if (!скаляры) try { скаляры = volume.voxelManager?.getCompleteScalarDataArray?.(); if (скаляры) источникСкаляров = 'voxelManager'; } catch (e) { /* */ }
    if (!скаляры) try { скаляры = volume.getScalarData?.(); if (скаляры) источникСкаляров = 'getScalarData'; } catch (e) { /* */ }
    if (!скаляры) return { ок: false, причина: 'объём есть, но пиксели недоступны' };

    const dims = volume.dimensions || volume.imageData?.getDimensions?.();
    const spacing = volume.spacing || volume.imageData?.getSpacing?.();
    const origin = volume.origin || volume.imageData?.getOrigin?.();
    let direction = volume.direction || volume.imageData?.getDirection?.();
    if (direction && direction.length !== 9) direction = null;
    if (!dims || !spacing || !origin) return { ок: false, причина: 'у объёма нет геометрии (размеры/шаг/начало)' };

    return {
      ок: true, скаляры, источникСкаляров,
      dims: Array.from(dims), spacing: Array.from(spacing), origin: Array.from(origin),
      direction: direction ? Array.from(direction) : null,
      подпись: `${volume.volumeId || 'том'}|${dims.join('x')}`,
    };
  } catch (e) {
    return { ок: false, причина: 'объём прочитать не удалось: ' + e.message };
  }
}

/** Подпись камеры: по ней видно, надо ли пересчитывать карту. */
function _подпись(cam, w, h) {
  if (!cam) return '';
  const ч = (v) => (typeof v === 'number' ? v.toFixed(2) : '?');
  return [cam.position, cam.focalPoint, cam.viewUp].map((a) => (a || []).map(ч).join(',')).join('|')
    + `|${ч(cam.parallelScale)}|${w}x${h}`;
}

/**
 * Один пересчёт: карта глубины + матрицы камеры в рендерер.
 * @returns {Promise<{ок: boolean, причина?: string}>}
 */
async function _пересчитатьВнутри({ принудительно = false } = {}) {
  if (!_viewport || !_cinematic) return { ок: false, причина: 'нечего пересчитывать: окно или рендерер не заданы' };
  const vp = _объёмноеОкно(_viewport);
  const холст = vp.canvas || vp.element?.querySelector('canvas');
  const ш = холст?.width || vp.element?.clientWidth || 0;
  const в = холст?.height || vp.element?.clientHeight || 0;
  if (!(ш > 0 && в > 0)) return { ок: false, причина: 'у объёмного окна нулевой размер' };

  let cam = null;
  try { cam = vp.getCamera?.(); } catch (e) { /* ниже отказ по причине */ }
  if (!cam) return { ок: false, причина: 'окно не отдало камеру' };

  const подпись = _подпись(cam, ш, в);
  if (!принудительно && подпись === _подписьКамеры) return { ок: true, повтор: true };

  // ── Матрицы камеры — в рендерер. Без них объёмный свет и тени не работают
  // вообще: они проверяют hasCameraMatrices(), а его никто не выставлял.
  try {
    const к = разобратьКамеру(cam, ш / в);
    if (к.ок) {
      const м = матрицыКамеры(к);
      _cinematic.setCameraMatrices?.(м.viewProj, к.eye);
    }
  } catch (e) { log.debug('[Глубина/камера]', e.message); }

  if (!_расчёт) _расчёт = new ГлубинаИзОбъёма();

  // ── Объём и его функция непрозрачности.
  const непроз = непрозрачностьОкна(vp);
  if (!непроз) {
    depthDiagnostics.isUsingRealDepth = false;
    depthDiagnostics.причина = 'объёмное окно не отдало функцию непрозрачности — '
      + 'считать глубину по чужой картинке нельзя';
    return { ок: false, причина: depthDiagnostics.причина };
  }
  const об = await _объёмОкна(vp);
  if (!об.ок) {
    depthDiagnostics.isUsingRealDepth = false;
    depthDiagnostics.причина = об.причина;
    return { ок: false, причина: об.причина };
  }

  // Диапазон данных — по разрежённой выборке: полный проход по 300 МБ ради
  // диагностики не нужен, а порядок величин виден и по каждому сотому значению.
  {
    const a = об.скаляры;
    let мин = Infinity, макс = -Infinity;
    const шаг = Math.max(1, Math.floor(a.length / 20000));
    for (let i = 0; i < a.length; i += шаг) { const v = a[i]; if (v < мин) мин = v; if (v > макс) макс = v; }
    depthDiagnostics.диапазонДанных = [мин, макс];
    depthDiagnostics.источникСкаляров = об.источникСкаляров;
    depthDiagnostics.диапазонТаблицы = непроз.область ? [непроз.область[0], непроз.область[1]] : null;
    const обл = непроз.область;
    if (обл && (макс < обл[0] || мин > обл[1])) {
      depthDiagnostics.isUsingRealDepth = false;
      depthDiagnostics.причина = `значения объёма (${мин}…${макс}) и область функции непрозрачности `
        + `(${обл[0]}…${обл[1]}) не пересекаются — глубина по ним была бы выдумкой`;
      try { _cinematic.setDepthSource?.(null); } catch (e) { /* */ }
      return { ок: false, причина: depthDiagnostics.причина };
    }
  }

  const t0 = performance.now();
  const загр = _расчёт.загрузитьОбъём({
    скаляры: об.скаляры, dims: об.dims, spacing: об.spacing, origin: об.origin,
    direction: об.direction, непрозрачность: непроз,
    // В подпись входит и непрозрачность: смена пресета меняет карту глубины.
    подпись: `${об.подпись}|${непроз(0).toFixed(3)}|${непроз(300).toFixed(3)}|${непроз(1000).toFixed(3)}`,
  });
  if (!загр.ок) {
    depthDiagnostics.isUsingRealDepth = false;
    depthDiagnostics.причина = загр.причина;
    try { _cinematic.setDepthSource?.(null); } catch (e) { /* */ }
    return { ок: false, причина: загр.причина };
  }

  const карта = _расчёт.посчитать({ камера: cam, ширина: ш, высота: в });
  if (!карта.ок) {
    depthDiagnostics.isUsingRealDepth = false;
    depthDiagnostics.причина = карта.причина;
    try { _cinematic.setDepthSource?.(null); } catch (e) { /* */ }
    return { ок: false, причина: карта.причина };
  }

  // Луч не попал НИ РАЗУ — значит камера, объём или порог не согласуются.
  // Отдавать карту промахов нельзя: она выглядит как «всё бесконечно далеко»,
  // и эффекты по ней сделают ерунду.
  if (карта.доляПопаданий < 0.005) {
    depthDiagnostics.isUsingRealDepth = false;
    depthDiagnostics.доляПопаданий = карта.доляПопаданий;
    depthDiagnostics.причина = `луч глубины попал в объём лишь в ${(карта.доляПопаданий * 100).toFixed(2)}% пикселей — карта не отдана`;
    try { _cinematic.setDepthSource?.(null); } catch (e) { /* */ }
    return { ок: false, причина: depthDiagnostics.причина };
  }

  _cinematic.setDepthSource?.(карта.данные, {
    width: карта.width, height: карта.height, isFloat: true,
    // Признак «глубина ЛИНЕЙНАЯ» важен: у аппаратной глубины разности между
    // соседними пикселями зависят от расстояния, у линейной — нет, и пороги
    // экранных эффектов задаются в долях диапазона near…far.
    линейная: true, near: карта.near, far: карта.far,
    // [10.19.21] Нормали из объёма — тем же заходом, что глубина: сырой RGBA8
    // (n·0,5+0,5, годность в альфе), рендерер грузит его в текстуру как есть.
    // Нет нормалей — эффекты по ним не включаются, глубина работает как раньше.
    нормали: карта.нормали || null,
    доляНормалей: карта.доляНормалей || 0,
  });

  _подписьКамеры = подпись;
  depthDiagnostics.isUsingRealDepth = true;
  depthDiagnostics.причина = '';
  depthDiagnostics.extractionTimeMs = performance.now() - t0;
  depthDiagnostics.карт++;
  depthDiagnostics.доляПопаданий = карта.доляПопаданий;
  depthDiagnostics.доляНормалей = карта.доляНормалей || 0;
  depthDiagnostics.размер = `${карта.width}×${карта.height}`;
  if (depthDiagnostics.карт === 1) {
    log.info(`[Глубина] карта ${карта.width}×${карта.height}, попаданий ${(карта.доляПопаданий * 100).toFixed(1)}%, `
      + `нормалей ${((карта.доляНормалей || 0) * 100).toFixed(1)}%, `
      + `${depthDiagnostics.extractionTimeMs.toFixed(0)} мс, диапазон ${карта.near.toFixed(1)}…${карта.far.toFixed(1)} мм`);
  }
  return { ок: true, доляПопаданий: карта.доляПопаданий, мс: depthDiagnostics.extractionTimeMs };
}

/**
 * Пересчёт с сообщением наружу: интерфейс должен узнавать о глубине из одного
 * места, а не опрашивать её по таймеру. Раньше переключатели «AO» и «Тени» стояли
 * выключенными жёстко в разметке — то есть даже при готовой глубине оставались
 * мёртвыми.
 */
export async function пересчитатьГлубину(нстр = {}) {
  const итог = await _пересчитатьВнутри(нстр);
  if (!итог.повтор) {
    try { emit(EVENTS.DEPTH_UPDATED, { ...depthDiagnostics, ок: !!итог.ок }); } catch (e) { log.debug('[Глубина/событие]', e.message); }
  }
  return итог;
}

/**
 * Подключить расчёт глубины к объёмному окну.
 * @returns {Promise<boolean>} подключились ли; причина отказа — в depthDiagnostics
 */
export async function attachDepthExtractor(viewport, cinematicRenderer) {
  if (!viewport || !cinematicRenderer) {
    log.warn('[Глубина] нет окна или рендерера');
    return false;
  }
  detachDepthExtractor();
  _viewport = viewport;
  _cinematic = cinematicRenderer;
  _active = true;

  // Пересчёт по событиям окна, но не чаще ПАУЗА_МС: во время вращения событий
  // десятки в секунду, а карта нужна одна.
  _onRenderHandler = () => {
    if (!_active) return;
    const сейчас = performance.now();
    if (сейчас - _последний < ПАУЗА_МС) {
      if (_таймер) return;
      _таймер = setTimeout(() => { _таймер = null; _onRenderHandler(); }, ПАУЗА_МС);
      return;
    }
    _последний = сейчас;
    пересчитатьГлубину().catch((e) => log.debug('[Глубина]', e.message));
  };

  const el = viewport.element;
  if (el) {
    el.addEventListener('cornerstoneimagerendered', _onRenderHandler);
    el.addEventListener('cornerstonevolumechanged', _onRenderHandler);
    el.addEventListener('cornerstonecameramodified', _onRenderHandler);
  }

  const первая = await пересчитатьГлубину({ принудительно: true });
  if (!первая.ок) {
    log.warn('[Глубина] пока недоступна: ' + (первая.причина || 'причина не названа'));
    // Подписку НЕ снимаем: объём может доехать позже (загрузка идёт частями), и
    // тогда первая же перерисовка окна даст карту. Но и врать, что глубина есть,
    // мы не будем — isUsingRealDepth остаётся false.
    return false;
  }
  log.info('[Глубина] подключено — расчёт по объёму окна');
  return true;
}

export function detachDepthExtractor() {
  if (_onRenderHandler && _viewport?.element) {
    _viewport.element.removeEventListener('cornerstoneimagerendered', _onRenderHandler);
    _viewport.element.removeEventListener('cornerstonevolumechanged', _onRenderHandler);
    _viewport.element.removeEventListener('cornerstonecameramodified', _onRenderHandler);
  }
  if (_таймер) { clearTimeout(_таймер); _таймер = null; }
  if (_cinematic) { try { _cinematic.setDepthSource(null); } catch (e) { /* */ } }
  if (_расчёт) { try { _расчёт.освободить(); } catch (e) { /* */ } _расчёт = null; }
  _active = false;
  _viewport = null;
  _cinematic = null;
  _onRenderHandler = null;
  _подписьКамеры = '';
  depthDiagnostics.isUsingRealDepth = false;
}

/**
 * Отладка луча в конкретном пикселе — числами, теми же формулами, что в
 * проверяемой математике. Пригодится, когда карта выглядит неправдоподобно.
 * @param {number} ndcX -1…1
 * @param {number} ndcY -1…1
 */
export function отладкаЛуча(ndcX = 0, ndcY = 0) {
  if (!_расчёт || !_viewport) return { ок: false, причина: 'расчёт глубины не поднят' };
  const vp = _viewport;
  const холст = vp.canvas || vp.element?.querySelector('canvas');
  let cam = null;
  try { cam = vp.getCamera?.(); } catch (e) { /* */ }
  if (!cam) return { ок: false, причина: 'окно не отдало камеру' };
  return _расчёт.отладкаЛуча({
    камера: cam, ширина: холст?.width || 1, высота: холст?.height || 1, ndcX, ndcY,
  });
}

export function isDepthActive() { return _active && depthDiagnostics.isUsingRealDepth; }
export function getDepthDiagnostics() { return { ...depthDiagnostics }; }

// [10.19.21] Сверка нормалей на сфере — наружу тем же путём, что остальная
// диагностика глубины: панели и консоли не нужно знать, в каком файле она живёт.
export { проверкаНормалейНаСфере };

export default {
  attachDepthExtractor, detachDepthExtractor, isDepthActive, getDepthDiagnostics,
  пересчитатьГлубину, отладкаЛуча, проверкаНормалейНаСфере, depthDiagnostics, ПАУЗА_МС,
};
